<template>
  <v-container fluid class="fill-height pa-0">
    <v-row no-gutters class="fill-height">
      <v-col class="dark-image-bg pa-6 hidden-sm-and-down">
        <v-row align="center" class="fill-height">
          <v-flex>
            <h1 class="welcome-message text-center pb-4 font-weight-regular" v-html="$t( 'message.ssoPanelTitle' )"></h1>
          </v-flex>
        </v-row>
      </v-col>
      <v-col align="center" class="pa-6 ma-auto white">
        <div class="sign-in-up pt-12 pt-sm-0 pt-md-0 pt-lg-0 pt-xl-0">
          <v-form lazy-validation @submit.prevent="submit">
            <h2 class="pb-1 mb-6 text-left font-weight-regular" v-html="$t( 'message.ssoLinkTitle' )"></h2>
            <div class="mb-6" v-if="email">
              <div class="user-identity-modern d-flex align-center justify-center pa-4 rounded-lg">
                <v-avatar color="primary" size="42" class="mr-4 elevation-1">
                  <span class="white--text font-weight-bold text-subtitle-1">
                    {{ avatarInitials }}
                  </span>
                </v-avatar>
                <div class="d-flex flex-column text-left overflow-hidden" style="max-width: 70%;">
                  <span class="text-subtitle-2 font-weight-bold grey--text text--darken-4 text-truncate">
                    {{ name }}
                  </span>
                  <span class="caption grey--text text--darken-1 text-truncate">
                    {{ email }}
                  </span>
                </div>
                <v-fade-transition>
                  <div v-if="name && email" class="d-flex align-center pl-4">
                    <v-icon color="primary" size="24">mdi-link-variant</v-icon>
                  </div>
                </v-fade-transition>
              </div>
            </div>
            <div class="mb-6 text-left">
              <v-text-field
                  v-model="password"
                  name="password"
                  :type="showPass ? 'text' : 'password'"
                  :disabled="loading"
                  :placeholder="$t('message.Password')"
                  persistent-hint
                  :rules="[rules.required]"
                  @keydown.enter="submit"
              >
                <template v-slot:append>
                  <v-icon
                      @click="showPass = !showPass"
                      color="grey darken-1"
                      style="cursor: pointer;"
                  >
                    {{ showPass ? 'mdi-eye-off' : 'mdi-eye' }}
                  </v-icon>
                </template>
              </v-text-field>
            </div>
            <p class="cvw-change-password">
              <a v-if="!mailInProgress" @click="requestChangePassword">{{ $t( 'message.IForgotMyPassword' ) }}</a>
              <span v-if="mailInProgress">...</span>
            </p>
            <p class="mt-7" v-bind:class="{ 'cvw-status-message': error }">{{ error }}</p>
            <div class="text-center mt-8">
              <v-btn
                  color="primary"
                  :loading="loading"
                  :disabled="!password || loading"
                  @click="submit"
                  style="min-width: 200px; height: 40px; font-size: 16px; text-transform: none; box-shadow: none;"
              >
                {{ $t( "message.ssoLinkSubmit" ) }}
              </v-btn>
            </div>
          </v-form>
        </div>
      </v-col>
    </v-row>
    <sh-app-footer class="pb-0"></sh-app-footer>
  </v-container>
</template>

<script>
    import AppFooter from "../../components/common/AppFooter";
    import {globalEvents, JSONPureClient} from "@cloudlace/client";
    import ssoSignupServiceMap from "@/api/sso-signup-service-map";
    import serviceSchema from "../../../node_modules/@cloudlace/vue-widgets/src/api/signedout-service-schema";

    const ssoSignupClient = new JSONPureClient( {
        service_url : process.env.VUE_APP_SHIO_CORE_SSO_SIGNUP_URL,
        service_map : ssoSignupServiceMap
    } );

    const signInClient = new JSONPureClient( {
        service_url : process.env.VUE_APP_USER_SIGNEDOUT_URL,
        service_map : serviceSchema
    } );

    export default {
        name : "SsoLinkAccountPane",
        components : {
            "sh-app-footer" : AppFooter,
        },
        data()
        {
            return {
                loading : false,
                error : null,
                link_token : null,
                email : null,
                username : null,
                name : null,
                password : "",
                mailInProgress : false,
                showPass : false,
                redirect_url : null,
                rules : {
                    required : v => !!v || this.$t( "message.ssoPasswordRequired" )
                }
            };
        },
        computed : {
            avatarInitials()
            {
                if( !this.name )
                {
                    return "?";
                }
                const parts = this.name.trim().split( " " );
                if( parts.length >= 2 )
                {
                    return (parts[ 0 ][ 0 ] + parts[ parts.length - 1 ][ 0 ]).toUpperCase();
                }
                return this.name.substring( 0, 1 ).toUpperCase();
            }
        },
        created()
        {
            this.link_token = this.$route.query.token;
            this.redirect_url = this.$route.query.redirect_url;
            if( !this.link_token )
            {
                this.error = this.$t( "message.Error!Unexpected" );
                return;
            }
            this.fetchSsoContext( this.link_token );
        },
        methods : {
            fetchSsoContext( token )
            {
                this.loading = true;
                ssoSignupClient.retrieveSsoContext( token ).then( result =>
                {
                    this.name = result.name;
                    this.username = result.username;
                    this.email = result.email;
                    this.loading = false;
                } ).catch( () =>
                {
                    this.loading = false;
                    this.error = this.$t( "message.Error!Unexpected" );
                } )
            },
            submit()
            {
                if ( this.loading ) {
                    return;
                }
                this.error = null;
                this.loading = true;
                ssoSignupClient.createSsoUserWithPassword( this.link_token, this.password ).then( result =>
                {
                    this._handleSessionResult( result );
                } ).catch( err =>
                {
                    this.loading = false;
                    if( err.code_key === 401 )
                    {
                        this.error = this.$t( "message.Error!InvalidCredentials" );
                    }
                    else if( err.code_key === 403 )
                    {
                        const minutes = "" + Math.ceil( parseInt( err.user_msg.substring( err.user_msg.indexOf( ':' ) + 2 ) ) / 60 );
                        this.error = this.$t( "message.Error!TemporarilyLockedOut" ).replace( '${minutes}', minutes );
                    }
                    else
                    {
                        this.error = this.$t( "message.Error!Unexpected" );
                    }
                } )
            },
            requestChangePassword()
            {
                this.mailInProgress = true;
                signInClient.createPasswordUpdateToken( this.email, this.scope ).then( result =>
                {
                    this.mailInProgress = false;
                    this.$alert( {
                        text : this.$t( 'message.SignHeroSentPasswordResetRequest' ),
                        acceptText : this.$t( "signflow.Ok" )
                    } );
                } ).catch( err =>
                {
                    console.error( err );
                    this.mailInProgress = false;
                    this.statusMessage = this.labels[ 'Error!Unexpected' ];
                } );
            },
            _handleSessionResult( result )
            {
                if( result.scope.length === 0 )
                {
                    globalEvents.emit( "signed-out", {} );
                }
                else if( this.redirect_url )
                {
                    window.location.assign( this.redirect_url );
                }
                else
                {
                    window.$cc_user_info = result;
                    globalEvents.emit( "signed-in", result );
                }
            },
        }
    }
</script>

<style lang="sass" scoped>
  .relative
    position: relative

  .lh-relaxed
    line-height: 1.6

  .user-identity-modern
    background-color: #ffffff
    border: 1px solid #e0e6ed
    transition: all 0.2s ease-in-out

  .user-identity-modern:hover
    border-color: #b0b8c4
    background-color: #fafbfc
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05)

  .welcome-subtitle
    font-size: 24px
</style>